import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatChipsModule } from '@angular/material/chips';
import { MatDividerModule } from '@angular/material/divider';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { forkJoin } from 'rxjs';

import { InstructivoEmbarqueStateService, InstructivoEmbarqueData } from '../../../services/instructivo-embarque-state.service';
import { InstructivoEmbarqueService } from '../../../services/instructivo-embarque.service';
import {
    WizardValidationResultDto,
    CreateShippingInstructionDto,
    CreateShippingInstructionSpeciesDto,
    TypeOfShipment,
    AntecedentesCatalogsDto,
    ContainerPackageTypeDto,
    PalletPackageTypeDto,
    ColdTreatmentIndicatorDto,
    ControlledAtmosphereDto,
    VentilationUnitDto,
    SpeciesPackageTypeDto,
} from '@opb-workspace/shared';
import { Router } from '@angular/router';
import { loadStep4CatalogSnapshot, Step4CatalogSnapshot } from './step4-catalog-snapshot';
import { Step4Labels } from './step4-labels';
import { bookingNumberDisplayFromSummary } from '../step2-booking-organizaciones/step2-booking-summary.util';

const EMPTY_CATALOGS: AntecedentesCatalogsDto = {
    clauseOfSales: [],
    typeOfCargoes: [],
    methodOfPayments: [],
    modeOfSales: [],
    wayOfTransports: [],
    fobCurrencies: [],
    freightConditions: [],
};

@Component({
    selector: 'app-step4-revision',
    standalone: true,
    imports: [
        CommonModule,
        MatCardModule,
        MatButtonModule,
        MatIconModule,
        MatProgressSpinnerModule,
        MatChipsModule,
        MatDividerModule,
        MatSnackBarModule,
    ],
    templateUrl: './step4-revision.component.html',
    styleUrls: ['./step4-revision.component.scss'],
})
export class Step4RevisionComponent implements OnInit {
    TypeOfShipment = TypeOfShipment;
    readonly Labels = Step4Labels;

    isValidating = false;
    isSubmitting = false;
    validation: WizardValidationResultDto | null = null;
    submitError = '';
    submitSuccess = false;
    createdIds: number[] = [];
    idempotencyKey = '';

    catalogsLoading = true;
    catalogs: AntecedentesCatalogsDto = { ...EMPTY_CATALOGS };
    containerPackageTypes: ContainerPackageTypeDto[] = [];
    palletPackageTypes: PalletPackageTypeDto[] = [];
    coldTreatmentIndicators: ColdTreatmentIndicatorDto[] = [];
    controlledAtmospheres: ControlledAtmosphereDto[] = [];
    ventilationUnits: VentilationUnitDto[] = [];
    speciesPackageTypes: SpeciesPackageTypeDto[] = [];

    constructor(
        public stateService: InstructivoEmbarqueStateService,
        private readonly service: InstructivoEmbarqueService,
        private readonly router: Router,
        private readonly snackBar: MatSnackBar
    ) {}

    ngOnInit(): void {
        if (!this.idempotencyKey) {
            this.idempotencyKey = this.generateUUID();
            this.stateService.patchFormData({
                idempotencyKey: this.idempotencyKey,
            });
        }
        this.loadReferenceCatalogs();
        this.runValidation();
    }

    private loadReferenceCatalogs(): void {
        this.catalogsLoading = true;
        loadStep4CatalogSnapshot(this.service).subscribe({
            next: (r) => {
                this.catalogs = r.catalogs;
                this.containerPackageTypes = r.containerPackageTypes;
                this.palletPackageTypes = r.palletPackageTypes;
                this.coldTreatmentIndicators = r.coldTreatmentIndicators;
                this.controlledAtmospheres = r.controlledAtmospheres;
                this.ventilationUnits = r.ventilationUnits;
                this.speciesPackageTypes = r.speciesPackageTypes;
                this.catalogsLoading = false;
            },
            error: () => {
                this.catalogsLoading = false;
                this.snackBar.open(
                    'No se pudieron cargar todos los catálogos; revise valores en paso 3 si algo no aparece.',
                    'Cerrar',
                    { duration: 5000 },
                );
            },
        });
    }

    private generateUUID(): string {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
            const r = (Math.random() * 16) | 0;
            const v = c === 'x' ? r : (r & 0x3) | 0x8;
            return v.toString(16);
        });
    }

    runValidation(): void {
        this.isValidating = true;
        this.validation = null;
        const data = this.stateService.snapshot.formData;
        this.service.validate(data).subscribe({
            next: (result) => {
                this.validation = result;
                this.isValidating = false;
            },
            error: () => {
                this.isValidating = false;
            },
        });
    }

    get canSubmit(): boolean {
        return (
            this.validation?.isValid === true &&
            !this.isSubmitting &&
            !this.submitSuccess
        );
    }

    submitShippingInstruction(): void {
        if (this.isSubmitting) return;
        this.isSubmitting = true;
        this.submitError = '';

        const snap = this.stateService.snapshot;
        const sharedFormData = snap.formData;
        const instructivos = snap.instructivos;

        if (!instructivos.length) {
            this.isSubmitting = false;
            this.submitError = 'No hay instructivos para enviar.';
            return;
        }

        const requests = instructivos.map((panel: InstructivoEmbarqueData, i: number) => {
            const raw = {
                ...sharedFormData,
                consigneePartyId: panel.consigneePartyId!,
                consigneeName: panel.consigneeName ?? '',
                consigneeAddress: panel.consigneeAddress,
                notifyPartyId: panel.notifyPartyId!,
                notifyName: panel.notifyName ?? '',
                notifyAddress: panel.notifyAddress,
                // Ref. Cliente + Antecedentes Financieros: por instructivo desde
                // feat-ie-wizard-mejoras (puntos 2 y 3) — antes venían de `sharedFormData`.
                customerReference: panel.customerReference ?? '',
                typeOfCargoId: panel.typeOfCargoId!,
                methodOfPaymentId: panel.methodOfPaymentId!,
                modeOfSaleId: panel.modeOfSaleId!,
                clauseOfSaleId: panel.clauseOfSaleId!,
                freightCondition: panel.freightCondition!,
                palletsChep: panel.palletsChep,
                palletsChepQuantity: panel.palletsChepQuantity,
                items: panel.items ?? [],
                idempotencyKey: `${this.idempotencyKey}-${i}`,
            } as CreateShippingInstructionDto & { createdInternalReference?: string };
            const { createdInternalReference: _omit, ...dto } = raw;
            return this.service.create(dto as CreateShippingInstructionDto);
        });

        forkJoin(requests).subscribe({
            next: (results) => {
                this.isSubmitting = false;
                this.submitSuccess = true;
                this.createdIds = results.map((r) => r.id);
                if (results[0]) {
                    this.stateService.patchFormData({ createdInternalReference: results[0].internalReference });
                }
                const n = results.length;
                const allOk = results.every((r) => r.emailStatus === 'OK');
                const anyFailed = results.some((r) => r.emailStatus === 'FAILED');
                if (allOk) {
                    this.snackBar.open(`✓ ${n} instructivo${n === 1 ? '' : 's'} creado${n === 1 ? '' : 's'} y enviado${n === 1 ? '' : 's'} por email`, 'Cerrar', { duration: 5000, panelClass: ['snackbar-success'] });
                } else if (anyFailed) {
                    this.snackBar.open(`⚠️ Instructivo${n === 1 ? '' : 's'} creado${n === 1 ? '' : 's'}, algunos emails fallaron`, 'Cerrar', { duration: 6000, panelClass: ['snackbar-warning'] });
                } else {
                    this.snackBar.open(`✓ ${n} instructivo${n === 1 ? '' : 's'} creado${n === 1 ? '' : 's'}`, 'Cerrar', { duration: 5000, panelClass: ['snackbar-success'] });
                }
            },
            error: (err) => {
                this.isSubmitting = false;
                this.submitError = err?.status === 409
                    ? 'Error de duplicado al crear los instructivos.'
                    : 'Error al crear los instructivos. Intente nuevamente.';
                this.snackBar.open('❌ ' + this.submitError, 'Cerrar', { duration: 6000, panelClass: ['snackbar-error'] });
            },
        });
    }

    get formData() {
        return this.stateService.snapshot.formData;
    }

    /**
     * Texto «Sí/No» + cantidad opcional para PALLETS CHEP del resumen — por instructivo desde
     * feat-ie-wizard-mejoras (punto 3; antes era un valor único a nivel de booking).
     */
    palletsChepSummaryFor(instr: InstructivoEmbarqueData): string {
        if (instr.palletsChep === undefined || instr.palletsChep === null) {
            return '—';
        }
        const label = this.Labels.yn(instr.palletsChep);
        const qty = instr.palletsChepQuantity;
        return qty != null ? `${label} (${qty})` : label;
    }

    /**
     * REF. EMB. INTERNA: muestra la Ref. Cliente del primer instructivo (representativa del
     * booking en el resumen general; cada instructivo tiene la suya propia más abajo desde
     * feat-ie-wizard-mejoras punto 2). Post-submit usa el valor asignado por el backend si
     * está disponible.
     */
    get summaryInternalReference(): string {
        const created = this.formData.createdInternalReference?.trim();
        if (created) {
            return created;
        }
        return this.dash(this.stateService.snapshot.instructivos[0]?.customerReference);
    }

    /** Combina fijo y móvil en una sola línea legible. */
    phoneLine(
        a: string | null | undefined,
        b: string | null | undefined,
    ): string {
        const parts = [a, b]
            .map((x) => (x ?? '').trim())
            .filter((s) => s.length > 0);
        return parts.length ? parts.join(' · ') : '—';
    }

    get typeOfShipmentName(): string {
        const type = this.formData.typeOfShipmentId;
        return type === TypeOfShipment.Container ? 'Contenedor' : 'Pallet';
    }

    /** Objeto único para resolver textos desde ítems (template). */
    get catalogSnap(): Step4CatalogSnapshot {
        return {
            catalogs: this.catalogs,
            containerPackageTypes: this.containerPackageTypes,
            palletPackageTypes: this.palletPackageTypes,
            coldTreatmentIndicators: this.coldTreatmentIndicators,
            controlledAtmospheres: this.controlledAtmospheres,
            ventilationUnits: this.ventilationUnits,
            speciesPackageTypes: this.speciesPackageTypes,
        };
    }

    dash(text: string | number | null | undefined): string {
        if (text === null || text === undefined) {
            return '—';
        }
        const s = String(text).trim();
        return s.length ? s : '—';
    }

    /** Título estilo PDF: temporada aproximada según ETD (hemisferio sur). */
    get summarySeasonLine(): string {
        const raw = this.formData.etd;
        if (!raw) {
            return `TEMPORADA ${new Date().getFullYear()}`;
        }
        const d = new Date(raw);
        const m = d.getMonth();
        const y = d.getFullYear();
        const start = m >= 9 ? y : y - 1;
        return `TEMPORADA ${start} – ${start + 1}`;
    }

    /** Fecha del resumen (documento). */
    get summaryDocDate(): string {
        return new Date().toLocaleDateString('es-CL', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        });
    }

    formatDate(dateStr?: string | Date | null): string {
        if (!dateStr) {
            return '—';
        }
        const d = dateStr instanceof Date ? dateStr : new Date(dateStr);
        if (Number.isNaN(d.getTime())) {
            return '—';
        }
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear();
        const hours = String(d.getHours()).padStart(2, '0');
        const mins = String(d.getMinutes()).padStart(2, '0');
        return `${day}-${month}-${year} ${hours}:${mins}`;
    }

    get vesselVoyageLine(): string {
        const v = this.dash(this.formData.vesselName);
        const n = this.dash(this.formData.voyageNumber);
        if (v === '—' && n === '—') {
            return '—';
        }
        if (n === '—') {
            return v;
        }
        if (v === '—') {
            return n;
        }
        return `${v} — Viaje ${n}`;
    }

    /**
     * País destino final según POD (misma fuente que el paso 1: `voyage.countryOfFinalDestination`).
     */
    get summaryFinalDestinationCountryPerPod(): string {
        const fromVoyage = (
            this.stateService.snapshot.voyageData?.countryOfFinalDestination ??
            ''
        ).trim();
        if (fromVoyage) {
            return fromVoyage;
        }
        return (this.formData.countryOfFinalDestination ?? '').trim();
    }

    /** N° booking: estado del wizard o resolución desde listado del viaje / apply-booking. */
    get summaryBookingNumber(): string {
        const fd = this.formData;
        const direct = (fd.bookingNumber ?? '').trim();
        if (direct) {
            return direct;
        }
        const bid = fd.bookingId;
        const list = this.stateService.snapshot.voyageData?.availableBookings;
        if (bid != null && list?.length) {
            const row = list.find((b) => b.bookingId === bid);
            if (row) {
                const s = bookingNumberDisplayFromSummary(row).trim();
                if (s) {
                    return s;
                }
            }
        }
        return (this.stateService.snapshot.bookingData?.bookingNumber ?? '').trim();
    }

    /** Código / referencia operativa (ref. cliente del primer instructivo, o booking). */
    get summaryCodeLine(): string {
        const ref = this.stateService.snapshot.instructivos[0]?.customerReference?.trim();
        if (ref) {
            return ref;
        }
        return this.dash(this.summaryBookingNumber);
    }

    /** Texto único de observaciones (header + ítems + aduana). */
    get allObservationsText(): string {
        const parts: string[] = [];
        if (this.formData.remarks?.trim()) {
            parts.push(this.formData.remarks.trim());
        }
        return parts.length ? parts.join('\n\n') : '—';
    }

    /** `true` solo si hay algo real que mostrar (no se renderiza el bloque si no). */
    get hasObservations(): boolean {
        return this.allObservationsText !== '—';
    }

    /**
     * "Stacking" del resumen estilo documento: texto descriptivo del horario
     * (`WizardInitResponseDto.stackingDescription`, viene de Step 1) — no confundir con
     * `formData.cutOffFisico` (fecha única, mapea a `BeginStackingDate`).
     */
    get stackingDescription(): string {
        return this.dash(this.stateService.snapshot.voyageData?.stackingDescription);
    }

    /** Total de contenedores (o pallets) del instructivo, sumando todos sus ítems. */
    totalContainersOrPalletsFor(instr: InstructivoEmbarqueData): number {
        const items = instr.items ?? [];
        if (this.formData.typeOfShipmentId === TypeOfShipment.Pallet) {
            return items.reduce((sum, it) => sum + (Number(it.quantityOfPallets) || 0), 0);
        }
        return items.reduce((sum, it) => sum + (Number(it.quantityOfContainers) || 0), 0);
    }

    /** Especies de TODOS los ítems del instructivo, en una sola lista (tabla ESPECIES del resumen). */
    flattenedSpeciesFor(instr: InstructivoEmbarqueData): CreateShippingInstructionSpeciesDto[] {
        return (instr.items ?? []).flatMap((it) => it.species ?? []);
    }

    goToItinerarios(): void {
        this.stateService.reset();
        this.router.navigate(['/itinerarios']);
    }

    openCreatedShippingInstruction(): void {
        const first = this.createdIds[0];
        if (first) {
            this.router.navigate(['/instructivo-embarque/detalle', first]);
        }
    }

    get instructivos(): InstructivoEmbarqueData[] {
        return this.stateService.snapshot.instructivos;
    }
}
